import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../services/api';
import BackButton from '../../components/common/BackButton';
import { getBrowserInfo } from '../../utils/browserUtils';
import './ReportBug.css';

function ReportBug() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTimeoutRef = useRef(null);

  // Get the page URL from navigation state, fallback to referrer or origin
  const getPageUrl = () => {
    if (location.state?.fromPage) {
      return location.state.fromPage;
    }
    if (document.referrer) {
      return document.referrer;
    }
    return window.location.origin;
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    severity: 'medium',
    pageUrl: getPageUrl(),
    browserInfo: getBrowserInfo()
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Clean up redirect timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const idToken = auth.user.id_token;
      await apiService.createBugReport(idToken, formData);

      setSuccess(true);

      // Redirect to home after 4 seconds
      redirectTimeoutRef.current = setTimeout(() => {
        navigate('/');
      }, 4000);
    } catch (err) {
      console.error('Error submitting bug report:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <main className="main-content">
        <div className="report-bug-container">
          <div className="success-message-large">
            <div className="success-icon-large">✓</div>
            <h2>Bug Report Submitted!</h2>
            <p>Thank you for helping us improve CoinedIn. We'll review your report and get back to you if we need more information.</p>
            <p className="redirect-message">Redirecting you to home...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="main-content">
      <div className="report-bug-container">
        <BackButton />

        <div className="bug-report-header">
          <div className="bug-icon-large">🐛</div>
          <div>
            <h1 className="page-title">Report a Bug</h1>
            <p className="page-subtitle">Help us improve CoinedIn by reporting any issues you encounter</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bug-report-form">
          {/* Title */}
          <div className="form-group">
            <label htmlFor="title">Bug Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Brief summary of the issue"
              maxLength="255"
              required
              disabled={submitting}
            />
            <small>{formData.title.length}/255 characters</small>
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Detailed description of the bug"
              rows="4"
              required
              disabled={submitting}
            />
            <small>Provide as much detail as possible about the issue</small>
          </div>

          {/* Steps to Reproduce */}
          <div className="form-group">
            <label htmlFor="stepsToReproduce">Steps to Reproduce</label>
            <textarea
              id="stepsToReproduce"
              name="stepsToReproduce"
              value={formData.stepsToReproduce}
              onChange={handleChange}
              placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
              rows="4"
              disabled={submitting}
            />
            <small>List the steps to recreate the issue (optional but helpful)</small>
          </div>

          {/* Expected vs Actual Behavior */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="expectedBehavior">Expected Behavior</label>
              <textarea
                id="expectedBehavior"
                name="expectedBehavior"
                value={formData.expectedBehavior}
                onChange={handleChange}
                placeholder="What should happen?"
                rows="3"
                disabled={submitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="actualBehavior">Actual Behavior</label>
              <textarea
                id="actualBehavior"
                name="actualBehavior"
                value={formData.actualBehavior}
                onChange={handleChange}
                placeholder="What actually happens?"
                rows="3"
                disabled={submitting}
              />
            </div>
          </div>

          {/* Severity */}
          <div className="form-group">
            <label htmlFor="severity">Severity *</label>
            <select
              id="severity"
              name="severity"
              value={formData.severity}
              onChange={handleChange}
              required
              disabled={submitting}
            >
              <option value="low">Low - Minor inconvenience</option>
              <option value="medium">Medium - Affects functionality</option>
              <option value="high">High - Major functionality broken</option>
              <option value="critical">Critical - App unusable</option>
            </select>
          </div>

          {/* Page URL */}
          <div className="form-group">
            <label htmlFor="pageUrl">Page URL</label>
            <input
              type="text"
              id="pageUrl"
              name="pageUrl"
              value={formData.pageUrl}
              onChange={handleChange}
              placeholder="URL where the bug occurred"
              maxLength="512"
              disabled={submitting}
            />
            <small>The page where you encountered the issue (auto-detected)</small>
          </div>

          {/* Browser Info */}
          <div className="form-group">
            <label htmlFor="browserInfo">Browser/Device Info</label>
            <input
              type="text"
              id="browserInfo"
              name="browserInfo"
              value={formData.browserInfo}
              onChange={handleChange}
              maxLength="255"
              disabled={submitting}
            />
            <small>Your browser and device information (auto-detected)</small>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Bug Report'}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}

export default ReportBug;
